import {
    Box,
    HStack,
    IconButton,
    ModalCloseButton,
    Text,
    Textarea,
    VStack,
} from '@chakra-ui/react';
import { useQuery } from 'hooks/useCustomQuery';
import { useState } from 'react';
import { MdFileCopy } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from '@/components/v4';
import { axios } from '@/services/axios';

import Tooltip from 'components/v4/Tooltip';

const LogsModal = ({ isOpen, onClose, aorId, extensionName }) => {
    const [currentLimit, setCurrentLimit] = useState(10);
    const [hasShownMore, setHasShownMore] = useState(false);
    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);
    const noLogsText = 'No Logs available.';

    const {
        data: logsResponse,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['TRSRegistrationLogs', aorId, currentLimit],
        queryFn: async () => {
            if (!aorId) return null;

            try {
                const endpoint = `/TRSRegistration/${aorId}/Logs?limit=${currentLimit}`;
                const response = await axios.get(endpoint, {
                    headers: {
                        'X-RequestScope': requiredScope,
                    },
                });
                return response.data;
            } catch (error) {
                console.error('Error fetching TRS registrations logs: ', error);
                throw error;
            }
        },
        enabled: !!aorId && isOpen,
        refetchOnWindowFocus: false,
    });

    const processedLogs = logsResponse
        ? logsResponse.split('\n').filter((line) => line.trim())
        : [];
    const logsText = processedLogs.join('\n');
    const successToastMessage =
        logsResponse === noLogsText
            ? 'Copied to clipboard'
            : `${currentLimit === 10 && logsResponse ? 10 : processedLogs.length} logs copied to clipboard`;

    const handleCopyLogs = async () => {
        try {
            await navigator.clipboard.writeText(logsText);
            toast.success(successToastMessage, {
                autoClose: 2000,
            });
        } catch (error) {
            toast.error('Failed to copy logs', { autoClose: 3000 });
        }
    };

    const handleShowMore = () => {
        setCurrentLimit(100);
        setHasShownMore(true);
    };

    const handleClose = () => {
        setCurrentLimit(10);
        setHasShownMore(false);
        onClose();
    };

    const canShowMore =
        !hasShownMore && currentLimit === 10 && processedLogs.length >= 10;

    return (
        <Modal
            closeOnOverlayClick={false}
            isCentered
            isOpen={isOpen}
            onClose={handleClose}
            size="4xl">
            <ModalOverlay backdropFilter="blur(10px)" />
            <ModalContent minH="600px">
                <ModalHeader>
                    <HStack
                        justifyContent="space-between"
                        alignItems="center"
                        pr={8}>
                        <Text>Registration Logs</Text>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody py={0}>
                    <VStack spacing={4} align="stretch">
                        <Text
                            fontSize="sm"
                            color={darkMode ? 'gray.400' : 'gray.600'}>
                            Recent registration activity for extension{' '}
                            {extensionName}
                        </Text>
                        <Box>
                            <Textarea
                                value={
                                    isLoading
                                        ? 'Loading logs...'
                                        : logsText || 'No Logs available.'
                                }
                                readOnly
                                minHeight="400px"
                                fontFamily="mono"
                                fontSize="sm"
                                resize="vertical"
                                placeholder="No Logs available."
                                _dark={{ bg: 'gray.800' }}
                                borderColor="transparent"
                                _hover={{
                                    borderColor: 'transparent',
                                }}
                            />
                        </Box>
                        {currentLimit === 10 &&
                            processedLogs.length >= 10 &&
                            !hasShownMore && (
                                <Text
                                    fontSize="sm"
                                    color={darkMode ? 'gray.400' : 'gray.600'}
                                    textAlign="center">
                                    Showing first 10 logs. Click &quot;Show
                                    More&quot; to load up to 100 logs.
                                </Text>
                            )}
                        {hasShownMore && (
                            <Text
                                fontSize="sm"
                                color="gray.600"
                                textAlign="center">
                                Showing {processedLogs.length} logs (maximum
                                100)
                            </Text>
                        )}
                        {error && (
                            <Text
                                fontSize="sm"
                                color="red.500"
                                textAlign="center">
                                Error loading logs: {error.message}
                            </Text>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter justifyContent="space-between">
                    <HStack spacing={3}>
                        <Button
                            minW={120}
                            isDisabled={!canShowMore || isLoading}
                            isLoading={isLoading && currentLimit === 100}
                            onClick={handleShowMore}>
                            Show More
                        </Button>

                        <Tooltip label="Copy logs to clipboard">
                            <IconButton
                                icon={<MdFileCopy />}
                                aria-label="Copy logs"
                                variant="ghost"
                                color={darkMode ? 'white' : 'black'}
                                _hover={{
                                    color: 'brand.600',
                                }}
                                isDisabled={isLoading || !logsText}
                                onClick={handleCopyLogs}
                            />
                        </Tooltip>
                    </HStack>
                    <Button minW={120} variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default LogsModal;
